import { fetchGraphql, type GraphQLError } from '@zoopla/fetch-graphql';
import { FetchUserDocument, type FetchUserQuery } from '@zoopla/user-queries';

import { type SessionData } from './user.types';

import { getActiveSessionIdentifier } from './setActiveSession';

export const STORAGE_KEY_PREFIX = 'USER_';

const loggedOutResp: SessionData = {
    brandPrimary: null,
    creationDate: null,
    loggedIn: false,
    registered: null,
    registrationSources: null,
    senderPropertyStatus: null,
    status: 'anonymous',
    userId: null,
    userUuid: null
};

/**
 * Will read/write from browser cache. Use opt.skipCache = true to force new
 * data to be fetched and written to cache.
 */
export async function getUserData(opts?: {
    skipCache?: boolean;
    readOnly?: boolean;
    abortSignal?: AbortSignal;
}): Promise<{
    data?: { user?: SessionData | null };
    errors?: GraphQLError[];
}> {
    const key = getActiveSessionIdentifier();
    /* To ensure we are reading correct session data */
    const cacheKey = STORAGE_KEY_PREFIX + key;
    if (!opts?.readOnly) clearStaleData(cacheKey);

    if (!opts?.skipCache && key) {
        if (key === 'anon') {
            /* logged out user get preset response */
            return { data: { user: loggedOutResp }, errors: undefined };
        }

        const cachedUserData = getStateFromStorage(cacheKey);
        if (cachedUserData) {
            return { data: { user: cachedUserData }, errors: undefined };
        }
    }

    try {
        const result = await fetchGraphql<FetchUserQuery>(
            FetchUserDocument,
            undefined,
            { signal: opts?.abortSignal }
        );
        const { name, email, __typename, ...sessionData } =
            result.data?.user ?? {};
        return { data: { user: sessionData }, errors: result.errors };
    } catch (error) {
        return { errors: [] };
    }
}

function getStateFromStorage(key: string): SessionData | null {
    if (!key) return null;
    const data = window?.sessionStorage?.getItem(key);
    try {
        return data ? JSON.parse(data) : null;
    } catch (e) {
        return null;
    }
}

export function writeDataToStorage(key: string, data: SessionData | null) {
    if (data && key && window?.sessionStorage?.setItem)
        window?.sessionStorage?.setItem(key, JSON.stringify(data));
}

function clearStaleData(validKey: string) {
    if (window?.sessionStorage) {
        Object.keys(window.sessionStorage)
            ?.filter((k) => k.startsWith(STORAGE_KEY_PREFIX))
            ?.forEach((k) => {
                if (validKey && k === validKey) return;
                window.sessionStorage.removeItem(k);
            });
    }
}
